import { selectProfileAge } from '../../../accounts/selectors/profile-age.selector.ts';
import { selectProfileName } from '../../../accounts/selectors/profile-name.selector.ts';
import { PromptOptions, PromptType } from '../../../main/types/prompt-options.types.ts';
import { getAudioPlayerUtil } from '../../../main/utils/audio/audio-player.util.ts';
import { getLocaleUtil } from '../../../main/utils/locale.util.ts';
import { getReduxStoreUtil } from '../../../main/utils/redux-store.util.ts';

import { APP_AGE_GROUP } from '../../constants/app-age-group';
import { DEFAULT_CHILD_NAME } from '../../constants/child.ts';
import { selectAttemptsHistory } from '../../selectors/attempts-history.selector.ts';
import { selectCurrentLessonCharacter } from '../../selectors/current-lesson-character.selector.ts';

const ENCOURAGEMENT_ATTEMPT_GAP = 10;

export class EncouragementUtil {
  private static _instance: EncouragementUtil;

  private constructor(
    private readonly audioPlayerUtil = getAudioPlayerUtil(),
    private readonly localeUtil = getLocaleUtil(),
    private readonly reduxStoreUtil = getReduxStoreUtil()
  ) {}

  public static get instance(): EncouragementUtil {
    if (!EncouragementUtil._instance) {
      EncouragementUtil._instance = new EncouragementUtil();
    }
    return EncouragementUtil._instance;
  }

  public async shouldGiveEncouragement(): Promise<boolean> {
    const state = await this.reduxStoreUtil.getRootState();
    const attempts = state.activityTracking.attempts;
    return attempts.length >= ENCOURAGEMENT_ATTEMPT_GAP;
  }

  public async queueEncouragement(): Promise<void> {
    const state = await this.reduxStoreUtil.getRootState();

    const character = selectCurrentLessonCharacter(state);
    const history = selectAttemptsHistory(state);
    const name = selectProfileName(state);
    const age = selectProfileAge(state);

    const promptOptions: PromptOptions = {
      promptType: PromptType.ENCOURAGEMENT,
      metadata: {
        characterType: character?.characterType ?? '',
        attemptCount: state.activityTracking.attempts.length,
        correctAttempts: state.activityTracking.correctAttempts,
        history,
        name: name ?? DEFAULT_CHILD_NAME,
        age,
        ageGroup: APP_AGE_GROUP,
      },
    };

    this.audioPlayerUtil.generateAndSpeak(
      promptOptions,
      character,
      this.localeUtil.select('learn:encouragement_error_handling_message')
    );
  }
}

export const getEncouragementUtil = (): EncouragementUtil => EncouragementUtil.instance;
